import type { PropsWithChildren } from 'react';
import { View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { falSignature } from 'shared/@generated/icons/falSignature';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import { illustrationPalette as palette } from './illustration-palette';
import type { IllustrationScale } from './illustration-scene';
import {
  Caption,
  IllustrationFrame,
  Lines,
  Photo,
  useIllustrationCopy,
  type IllustrationProps,
} from './illustration-parts';

type Scaled = { s: IllustrationScale };

function FoldedPaper({ s, children }: PropsWithChildren<Scaled>) {
  const width = 120;
  const height = 136;
  const border = 0.9;
  const inset = border / 2;
  const foldSize = 17;
  const foldStart = width - foldSize;
  const outline = `M4 ${inset} H${foldStart} L${width - inset} ${foldSize} V${height - 4} Q${width - inset} ${height - inset} ${width - 4} ${height - inset} H4 Q${inset} ${height - inset} ${inset} ${height - 4} V4 Q${inset} ${inset} 4 ${inset} Z`;
  return (
    <View
      testID="folded-paper"
      className="absolute overflow-hidden"
      style={{
        left: s(20),
        top: s(12),
        width: s(width),
        height: s(height),
        padding: s(12 + border),
        backgroundColor: 'transparent',
        transform: [{ rotate: '-2deg' }],
      }}
    >
      <View testID="folded-paper-background" pointerEvents="none" className="absolute inset-0">
        <Svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
          <Path d={outline} fill={palette.paper} />
          <Path
            d={`M${foldStart} ${inset} L${width - inset} ${foldSize} H${foldStart} Z`}
            fill={palette.creamDark}
          />
          <Path
            d={`M${foldStart} ${inset} V${foldSize} H${width - inset}`}
            fill="none"
            stroke={palette.ink + '50'}
            strokeWidth={0.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            d={outline}
            fill="none"
            stroke={palette.ink + '80'}
            strokeWidth={border}
            strokeLinejoin="round"
          />
        </Svg>
      </View>
      {children}
    </View>
  );
}

function Rule({ s }: Scaled) {
  return <View style={{ height: s(0.7), backgroundColor: palette.ink + '25' }} />;
}

function Signature({ s, width, buyer = false }: Scaled & { width: number; buyer?: boolean }) {
  const t = useIllustrationCopy();
  return (
    <View style={{ width: s(width), gap: s(2) }}>
      <Icon
        icon={falSignature}
        className={buyer ? 'text-brand-brown-dark' : 'text-brand-green-dark'}
        style={{ width: s(27), height: s(15) }}
      />
      <Rule s={s} />
      <Caption s={s} size={6}>
        {t(buyer ? 'buyer_name' : 'seller')}
      </Caption>
    </View>
  );
}

function Contract({ s }: Scaled) {
  const t = useIllustrationCopy();
  const width = 94;
  const title = t('contract');
  return (
    <View style={{ gap: s(6) }}>
      <Text
        className="font-semibold"
        numberOfLines={1}
        style={{
          fontSize: s(title.length > 13 ? 8 : 10),
          lineHeight: s(14.5),
          color: palette.ink,
        }}
      >
        {title}
      </Text>
      <View className="flex-row items-center" style={{ width: s(width), gap: s(6) }}>
        <Photo s={s} width={35} height={31} tone="clay" />
        <View style={{ flex: 1, gap: s(2) }}>
          <Caption s={s} size={8}>
            {t('pet')}
          </Caption>
          <Caption s={s} size={6.2} muted>
            {t('breed')}
          </Caption>
          <Caption s={s} size={6.8} green>
            {t('price')}
          </Caption>
        </View>
      </View>
      <Lines s={s} width={width} rows={4} />
      <View className="flex-row justify-between">
        <Signature s={s} width={width * 0.43} />
        <Signature s={s} width={width * 0.43} buyer />
      </View>
    </View>
  );
}

export function PurchaseAgreement({ transparent = false }: IllustrationProps) {
  return (
    <IllustrationFrame transparent={transparent}>
      {(s) => (
        <FoldedPaper s={s}>
          <Contract s={s} />
        </FoldedPaper>
      )}
    </IllustrationFrame>
  );
}
